import { memo, useCallback, useEffect, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { TrainingField } from 'types/fields'
import {
    createTrainingField,
    deleteTrainingField,
    editTrainingField,
    getTrainingFieldById,
    getTrainingFields,
} from 'services/training-fields'
import { TrainingFieldsTable } from '../../components/training-fields-table'
import { TrainingFieldForm } from '../../components/training-field-form'

export const TrainingFields = memo(() => {
    const [fields, setFields] = useState<TrainingField[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [field, setField] = useState<TrainingField>({} as TrainingField)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getTrainingFields()
        }

        getAll()
            .then(data => {
                setFields(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setField({} as TrainingField)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getTrainingFieldById(id)
                .then(item => {
                    setField(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteTrainingField(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: TrainingField) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (field.id) {
                editTrainingField({
                    ...submitValues,
                    id: field.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createTrainingField(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, field, toggleModal]
    )

    const editMode = !!field.id
    const modalTitle = `${editMode ? `Edit training field ${field.name}` : `Add new training field`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Training fields
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <TrainingFieldsTable data={fields} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <TrainingFieldForm item={field} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
